import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { LoadingGrid } from '../../../../../rbts/helpers';
import { useObtenerEventoBoletera } from '../hooks/EventosBoleteraHook';
import { DatosYMapa } from '../tabs/DatosYMapa';
import { NivelesEntrada } from '../tabs/NivelesEntrada';
import { SubseccionesAsientos } from '../tabs/SubseccionesAsientos';
import { AforoVentas } from '../tabs/AforoVentas';
import { cambiarEstadoEvento } from '../../../../service/boletera/EventosBoletera';
import { SimpleAlert } from '../../../../../rbts/helpers';

type Params = { id: string };

const TABS = [
  { key: 'datos',        label: 'Datos y mapa' },
  { key: 'niveles',      label: 'Niveles de entrada' },
  { key: 'subsecciones', label: 'Sub-secciones y asientos' },
  { key: 'aforo',        label: 'Aforo y ventas' },
];

const STATUS_BADGE: Record<string, string> = {
  ACTIVE: 'badge-light-success',
  PEND:   'badge-light-warning',
  PAUSE:  'badge-light-secondary',
  FINISH: 'badge-light-danger',
};

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Publicado',
  PEND:   'Borrador',
  PAUSE:  'Pausado',
  FINISH: 'Finalizado',
};

const Detail: React.FC = () => {
  const { id }    = useParams<Params>();
  const eventoId  = Number(id);
  const history   = useHistory();
  const [reload, setReload]           = useState(0);
  const { evento, loading }           = useObtenerEventoBoletera(eventoId, reload);
  const [activeTab, setActiveTab]     = useState('datos');
  const [loadingEstado, setLoadingEstado] = useState(false);

  const handleEstado = async (accion: 'publicar' | 'pausar' | 'despublicar') => {
    setLoadingEstado(true);
    try {
      await cambiarEstadoEvento(eventoId, accion);
      setReload((r) => r + 1);
    } catch (error: any) {
      SimpleAlert(error?.response?.data?.message || 'Error al cambiar el estado del evento.', 4000, 'error');
    } finally {
      setLoadingEstado(false);
    }
  };

  if (loading) return <LoadingGrid text="Cargando evento..." />;
  if (!evento) return <div className="text-center text-muted py-15">Evento no encontrado.</div>;

  const statusCode     = evento.status?.code || 'PEND';
  const fechaFormateada = evento.fecha_inicio
    ? new Date(evento.fecha_inicio).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  return (
    <div className="card card-xxl-stretch">
      <div className="card-header border-0 pt-5 mt-2">
        <button className="btn btn-sm btn-light me-3" onClick={() => history.push('/boletera/eventos/')}>
          ← Volver a Eventos
        </button>
      </div>
      <div className="card-body pt-0">
        {/* Header del evento */}
        <div className="d-flex align-items-start justify-content-between mb-6 flex-wrap gap-3">
          <div>
            <h2 className="fw-bolder mb-1">
              {evento.nombre}
              <span className={`badge ms-3 fs-7 ${STATUS_BADGE[statusCode] || 'badge-light'}`}>
                {STATUS_LABEL[statusCode] || statusCode}
              </span>
            </h2>
            <div className="text-muted fs-6">
              {evento.recinto?.nombre || '—'} · {fechaFormateada}
            </div>
          </div>

          {/* Botones de estado — no se muestran en FINISH */}
          {statusCode !== 'FINISH' && (
            <div className="d-flex gap-2 flex-wrap">
              {statusCode === 'PEND' && (
                <button
                  className="btn btn-sm btn-success"
                  disabled={loadingEstado}
                  onClick={() => handleEstado('publicar')}
                >
                  Publicar
                </button>
              )}
              {statusCode === 'ACTIVE' && (
                <button
                  className="btn btn-sm btn-warning"
                  disabled={loadingEstado}
                  onClick={() => handleEstado('pausar')}
                >
                  Pausar ventas
                </button>
              )}
              {statusCode === 'PAUSE' && (
                <button
                  className="btn btn-sm btn-success"
                  disabled={loadingEstado}
                  onClick={() => handleEstado('publicar')}
                >
                  Reanudar ventas
                </button>
              )}
              {(statusCode === 'ACTIVE' || statusCode === 'PAUSE') && (
                <button
                  className="btn btn-sm btn-secondary"
                  disabled={loadingEstado}
                  onClick={() => handleEstado('despublicar')}
                >
                  Despublicar
                </button>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs nav-line-tabs nav-line-tabs-2x mb-6 fs-6">
          {TABS.map((tab) => (
            <li className="nav-item" key={tab.key}>
              <a
                className={`nav-link ${activeTab === tab.key ? 'active fw-bold' : 'text-muted'}`}
                href="#"
                onClick={(e) => { e.preventDefault(); setActiveTab(tab.key); }}
              >
                {tab.label}
              </a>
            </li>
          ))}
        </ul>

        {/* Contenido del tab activo */}
        <div className="tab-content">
          {activeTab === 'datos'        && <DatosYMapa evento={evento} onSaved={() => setReload((r) => r + 1)} />}
          {activeTab === 'niveles'      && <NivelesEntrada eventoId={eventoId} />}
          {activeTab === 'subsecciones' && <SubseccionesAsientos eventoId={eventoId} />}
          {activeTab === 'aforo'        && <AforoVentas eventoId={eventoId} />}
        </div>
      </div>
    </div>
  );
};

export { Detail };
